import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';

import {PermissionsTab} from 'src/admin-rights-service/modules/permissions';
import {ProfileHistory} from 'src/admin-rights-service/modules/role-profile/profile-history';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {DISCARD_CHANGES_SUCCESS_MESSAGE, ROUTES, STACKOVERFLOW_MESSAGE} from 'src/constants';
import {useBackgroundTaskManager} from 'src/contexts/background-task-context';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';

import Button from '@pb/kit-component-button';
import {Dialog, DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {StickyElement} from '@pb/kit-component-sticky-element';
import {Textarea} from '@pb/kit-component-textarea-v2';

import {ProfileTabs} from '../components/tabs';
import {
    SAVE_ROLE_PROFILE_REQUEST,
    SAVE_ROLE_PROFILE_SUCCESS,
    SAVE_ROLE_PROFILE_TASK,
} from '../constants';
import {profileHistoryActions} from '../profile-history/slices/profile-history.slice';

import {ProfileHeadline} from './profile-headline';
import {useRoleProfile} from './useRoleProfile';


import type {AccessRight} from 'src/hooks/access-rights/constants';


const RoleProfileSettings = () => {
    const navigate = useNavigate();
    const queryParams = useParams<{roleId: string}>();
    const profileId = queryParams.roleId ? +queryParams.roleId : -1;

    const {roleUpdate} = useEventTracking();
    const dispatch = useDispatch();
    const createBackgroundTask = useBackgroundTaskManager();
    const {setSuccessMessage} = useContext(MessageContext);

    const [saveNotes, setSaveNotes] = useState('');
    const [busy, setBusy] = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false);

    const {
        isLoading: isProfileLoading,
        loadingError: profileLoadingError,
        data: profile,
        dataInitial: initialProfile,
        setData: setProfile,
        isChanged: isProfileChanged,
        saveChanges,
        discardChanges,
        validateProfileName,
        validateDescription,
        validatePermissions,
    } = useRoleProfile({profileId, notes: saveNotes});

    const {setIsChunkChanged, proceed} = useConfirmationChunk<number>({
        chunkName: 'role-profile',
        onProceed: () => {
            dispatch(profileHistoryActions.requireRefresh());
        },
    });

    useEffect(() => {
        setIsChunkChanged(isProfileChanged);
    }, [isProfileChanged, setIsChunkChanged]);

    const openSaveModal = useCallback(() => {
        setShowSaveModal(true);
    }, []);

    const closeSaveModal = useCallback(() => {
        setShowSaveModal(false);
    }, []);

    const cancelSaveOperation = useCallback(() => {
        closeSaveModal();
        setSaveNotes('');
    }, [closeSaveModal]);

    const saveProfile = useCallback(async () => {
        closeSaveModal();
        setSaveNotes('');
        const task = createBackgroundTask(
            SAVE_ROLE_PROFILE_TASK,
            <b>{SAVE_ROLE_PROFILE_REQUEST}</b>,
            {
                maxStack: 1,
                stackOverflowMessage: STACKOVERFLOW_MESSAGE,
            },
        );

        if (task) {
            setBusy(true);
            const error = await saveChanges();
            setBusy(false);

            if (error) {
                task.completeFailure(<b>{error}</b>);
            } else {
                task.completeSuccess(<b>{SAVE_ROLE_PROFILE_SUCCESS}</b>);
                proceed();
            }
        }
    }, [saveChanges, closeSaveModal, createBackgroundTask, proceed]);

    const setActiveTab = useCallback((tab: string, recordId?: number) => {
        if (recordId) {
            navigate(`${ROUTES.PROFILE}/${profileId}/${tab}/${recordId}`);
        } else {
            navigate(`${ROUTES.PROFILE}/${profileId}/${tab}`);
        }
    }, [profileId, navigate]);

    const setProfileName = useCallback((profileName: string) => {
        validateProfileName(profileName, initialProfile.profileName);

        setProfile({
            ...profile,
            profileName,
        });

        roleUpdate({
            parameterName: 'Role Name',
            targetRoleId: profileId,
        });
    }, [profile, profileId, initialProfile, roleUpdate, setProfile, validateProfileName]);

    const setProfileDescription = useCallback((description: string) => {
        validateDescription(description);

        setProfile({
            ...profile,
            profileDescription: description,
        });
        roleUpdate({
            parameterName: 'Role Description',
            targetRoleId: profileId,
        });
    }, [profile, profileId, roleUpdate, setProfile, validateDescription]);

    const setProfilePermissions = useCallback((permissions: string[]) => {
        validatePermissions(permissions);

        setProfile({
            ...profile,
            rightsCodes: permissions,
        });

        roleUpdate({
            parameterName: 'Role Permissions',
            targetRoleId: profileId,
        });
    }, [profile, profileId, roleUpdate, setProfile, validatePermissions]);

    const changeSaveNotes = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setSaveNotes(event.target.value);
    }, []);

    const blurSaveNotes = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
        setSaveNotes(event.currentTarget.value.trim());
    }, []);

    return (
        <ServerResponseHandler
            serverError={profileLoadingError}
            sectionName="Role Permissions"
            accessRight={ACCESS_RIGHTS.MANAGE_PERMISSIONS_FOR_A_PROFILE as AccessRight}>
            <div>
                <StickyElement>
                    <ProfileHeadline
                        isSaveBusy={busy}
                        isProfileChanged={isProfileChanged}
                        isLoading={isProfileLoading}

                        profileId={profileId}
                        profile={profile}

                        onProfileNameSet={setProfileName}
                        onSave={openSaveModal}
                        onDiscard={() => {
                            discardChanges();
                            setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
                        }}
                    />
                    <ProfileTabs activeTab="permissions" onTabChange={setActiveTab}/>
                </StickyElement>
                <PermissionsTab
                    isLoading={isProfileLoading}
                    profile={profile}
                    onSetDescription={setProfileDescription}
                    onSetPermissions={setProfilePermissions}
                    profileHistory={profile.profileId >= 0 && <ProfileHistory/>}
                />
                <Dialog
                    shown={showSaveModal}
                    setShown={cancelSaveOperation}
                    wrapperProps={{style: {width: 600}}}>
                    <DialogHeader onClose={cancelSaveOperation}>
                        Save Role
                    </DialogHeader>
                    <DialogMain style={{padding: 24}}>
                        <Item>
                            <Label>Reason</Label>
                            <Textarea
                                resizable={false}
                                maxLength={100}
                                value={saveNotes}
                                onChange={changeSaveNotes}
                                onBlur={blurSaveNotes}
                            />
                        </Item>
                    </DialogMain>
                    <DialogFooter>
                        <Group>
                            <Button
                                variant="Type.PRIMARY"
                                disabled={!saveNotes}
                                onClick={saveProfile}>
                                Save
                            </Button>
                            <Button
                                variant="Type.SECONDARY"
                                onClick={cancelSaveOperation}>
                                Cancel
                            </Button>
                        </Group>
                    </DialogFooter>
                </Dialog>
            </div>
        </ServerResponseHandler>
    );
};

export const ValidatedRoleProfileSettings = () => (
    <ValidationContextProvider>
        <RoleProfileSettings/>
    </ValidationContextProvider>
);
